import { supabase } from '../lib/supabase';

interface ACHPaymentResult {
  success: boolean;
  transaction_id?: string;
  valmar_transaction_id?: string;
  error?: string;
}

export const processACHPayment = async (
  payrollEntryId: string,
  employeeId: string,
  amount: number
): Promise<ACHPaymentResult> => {
  try {
    console.log('[ACH Service] Starting processACHPayment', { payrollEntryId, employeeId, amount });

    const { data: session } = await supabase.auth.getSession();
    if (!session.session) {
      console.error('[ACH Service] Not authenticated - no session');
      throw new Error('Not authenticated');
    }

    console.log('[ACH Service] Session found, token length:', session.session.access_token.length);

    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const functionUrl = `${supabaseUrl}/functions/v1/process-ach-payment`;

    console.log('[ACH Service] Calling edge function:', functionUrl);
    console.log('[ACH Service] Request payload:', { payrollEntryId, employeeId, amount });

    const response = await fetch(functionUrl, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session.session.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        payrollEntryId,
        employeeId,
        amount,
      }),
    });

    console.log('[ACH Service] Response status:', response.status);
    console.log('[ACH Service] Response ok:', response.ok);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('[ACH Service] Error response body:', errorText);

      let errorData;
      try {
        errorData = JSON.parse(errorText);
      } catch {
        errorData = { raw: errorText };
      }

      console.error('[ACH Service] Parsed error data:', errorData);
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    console.log('[ACH Service] Success response:', JSON.stringify(result, null, 2));

    if (!result.success) {
      console.error('[ACH Service] Function returned success:false', result);
    }

    if (result.transaction_ids && result.transaction_ids.length > 0) {
      console.log('[ACH Service] Transaction IDs created:', result.transaction_ids);
    } else {
      console.warn('[ACH Service] WARNING: No transaction IDs in response!', result);
    }

    return result;
  } catch (error) {
    console.error('[ACH Service] Exception caught:', error);
    console.error('[ACH Service] Error stack:', error instanceof Error ? error.stack : 'No stack');
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred',
    };
  }
};

export const checkACHEnabled = async (): Promise<boolean> => {
  try {
    console.log('[ACH Service] Checking if ACH is enabled...');
    const { data, error } = await supabase
      .from('company_settings')
      .select('ach_enabled, valmar_username, valmar_password, valmar_merchant_id, valmar_environment')
      .single();

    if (error) {
      console.error('[ACH Service] Error checking ACH status:', error);
      return false;
    }

    console.log('[ACH Service] Company settings:', {
      ach_enabled: data?.ach_enabled,
      has_username: !!data?.valmar_username,
      has_password: !!data?.valmar_password,
      has_merchant_id: !!data?.valmar_merchant_id,
      environment: data?.valmar_environment
    });

    return data?.ach_enabled || false;
  } catch (error) {
    console.error('[ACH Service] Exception checking ACH status:', error);
    return false;
  }
};

export const getEmployeeBankingStatus = async (employeeId: string): Promise<boolean> => {
  try {
    console.log(`[ACH Service] Checking banking status for employee: ${employeeId}`);
    const { data, error } = await supabase
      .from('employee_banking')
      .select('id, account_label, allocation_type, allocation_value, priority')
      .eq('employee_id', employeeId)
      .eq('is_active', true);

    if (error) {
      console.error(`[ACH Service] Error checking banking status for ${employeeId}:`, error);
      return false;
    }

    const hasBanking = data && data.length > 0;
    console.log(`[ACH Service] Employee ${employeeId} has ${data?.length || 0} active bank account(s):`, data);

    return hasBanking;
  } catch (error) {
    console.error(`[ACH Service] Exception checking banking status for ${employeeId}:`, error);
    return false;
  }
};
